import { supabase } from '../lib/supabase';

export const customFetch = async (url: string, options: RequestInit): Promise<Response> => {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      signal: options?.signal,
      headers: {
        ...options?.headers,
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      // Retry on network errors or 5xx server errors
      if (response.status >= 500 || response.status === 0) {
        throw new Error('Network error occurred');
      }
    }
    return response;
  } catch (error) {
    throw error;
  }
};

// Exemplo de uso com o cliente Supabase
export const apiService = {
  async fetch<T>(url: string, options: RequestInit): Promise<T> {
    const response = await customFetch(url, options);
    return response.json();
  },
  
  // Outros métodos que usam o cliente Supabase diretamente
  async getData<T>(path: string): Promise<T> {
    const { data, error } = await supabase
      .from(path)
      .select('*');
      
    if (error) throw error;
    return data as T;
  }
}; 

// Serviço para projetos e membros
export const projectService = {
  // Buscar projetos ativos
  async getActiveProjects(organizationId: string) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('organization_id', organizationId)
        .eq('status', 'active')
        .order('name');
        
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Erro ao buscar projetos:', err);
      throw err;
    }
  },
  
  // Buscar membros de um projeto com validação adicional
  async getProjectMembers(projectId: string, organizationId: string) {
    try {
      console.log('Fetching project members with params:', { projectId, organizationId });
      
      const { data, error } = await supabase
        .from('project_team_members')
        .select('id, name, role, auth_id')
        .eq('project_id', projectId)
        .eq('organization_id', organizationId)
        .eq('status', 'active')
        .order('name');
        
      if (error) {
        console.error('Error fetching project members:', error);
        throw error;
      }
      
      console.log('Project members data:', data);
      
      // Modificar a filtragem para usar auth_id
      if (data) {
        const membersWithoutAuthId = data.filter(member => !member.auth_id);
        if (membersWithoutAuthId.length > 0) {
          console.warn(`${membersWithoutAuthId.length} members without auth_id will not be assignable to tasks:`, 
            membersWithoutAuthId.map(m => m.name));
        }
      }
      
      return data || [];
    } catch (err) {
      console.error('Exception when fetching project members:', err);
      throw err;
    }
  },
  
  // Buscar detalhes de um membro específico
  async getMemberDetails(memberId: string) {
    if (!memberId) return null;
    
    try {
      // Primeiro, tentar buscar como um membro de projeto
      const { data: projectMember, error: projectError } = await supabase
        .from('project_team_members')
        .select('id, name, role')
        .eq('id', memberId)
        .single();
        
      if (!projectError && projectMember) {
        return projectMember;
      }
      
      // Se não encontrar como membro de projeto, tentar buscar no user_profile
      const { data: user, error: userError } = await supabase
        .from('user_profile')
        .select('auth_id, name, email')
        .eq('auth_id', memberId)
        .single();

      if (!userError && user) {
        return {
          id: user.auth_id,
          name: user.name || user.email,
          role: 'Usuário'
        };
      }
      
      return null;
    } catch (err) {
      console.error('Erro ao buscar detalhes do membro:', err);
      return null;
    }
  },
  
  // Buscar atividades de um projeto específico
  async getProjectActivities(projectId: string, organizationId: string) {
    try {
      const { data, error } = await supabase
        .from('activity_cards')
        .select(`
          *,
          activity_columns (name),
          activity_card_members (
            id, user_id, role
          ),
          activity_card_checklists (
            id,
            activity_card_checklist_items (
              id, content, is_completed
            )
          ),
          activity_card_label_assignments (
            activity_card_labels (
              id, name, color
            )
          )
        `)
        .eq('organization_id', organizationId)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Erro ao buscar atividades do projeto:', err);
      throw err;
    }
  },
  
  // Buscar estatísticas de atividades por projeto
  async getProjectActivityStats(organizationId: string) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          id, 
          name,
          activity_cards (
            id,
            column_id,
            activity_columns (
              name,
              is_done
            )
          )
        `)
        .eq('organization_id', organizationId)
        .eq('status', 'active');
        
      if (error) throw error;
      
      // Processar os dados para calcular estatísticas
      return (data || []).map(project => {
        const totalActivities = project.activity_cards?.length || 0;
        const completedActivities = project.activity_cards?.filter(
          card => card.activity_columns?.is_done
        ).length || 0;
        
        return {
          id: project.id,
          name: project.name,
          totalActivities,
          completedActivities,
          progress: totalActivities > 0 
            ? Math.round((completedActivities / totalActivities) * 100) 
            : 0
        };
      });
    } catch (err) {
      console.error('Erro ao buscar estatísticas de atividades:', err);
      throw err;
    }
  },
  
  // Buscar todos os membros da organização (para atribuição de tarefas)
  async getOrganizationMembers(organizationId: string) {
    try {
      console.log('[Debug] Buscando todos os membros da organização:', organizationId);
      
      const { data, error } = await supabase
        .from('user_profile')
        .select(`
          id,
          name,
          email,
          auth_id,
          organization_id
        `)
        .eq('organization_id', organizationId)
        .not('auth_id', 'is', null)
        .eq('archived', false)
        .order('name');
        
      if (error) {
        console.error('[Debug] Erro ao buscar membros da organização:', error);
        throw error;
      }
      
      console.log('[Debug] Membros da organização encontrados (brutos):', data);
      
      // Verificar cada membro para garantir que auth_id é um UUID válido
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      
      // Formatar os membros para o formato esperado pela interface
      const formattedMembers = (data || [])
        .filter(member => member.auth_id && uuidRegex.test(member.auth_id)) // Filtrar apenas membros com auth_id válido
        .map(member => {
          return {
            id: member.id.toString(),
            name: member.name || member.email || 'Usuário sem nome',
            role: 'Membro da Organização',
            auth_id: member.auth_id,
            email: member.email
          };
        });
      
      console.log('[Debug] Membros da organização válidos:', 
        formattedMembers.map(m => ({ id: m.id, name: m.name, auth_id: m.auth_id }))
      );
      
      return formattedMembers;
    } catch (err) {
      console.error('[Debug] Erro ao buscar membros da organização:', err);
      throw err;
    }
  }
}; 

// Adicionar função para verificar se um usuário existe
export const userService = {
  async checkUserExists(userId: string) {
    try {
      const { data, error } = await supabase
        .from('user_profile')
        .select('auth_id')
        .eq('auth_id', userId)
        .single();
        
      if (error) {
        console.error('Error checking user existence:', error);
        return false;
      }
      
      return !!data;
    } catch (err) {
      console.error('Exception when checking user existence:', err);
      return false;
    }
  },
  
  // Novo método para buscar detalhes de um usuário
  async getUserDetails(userId: string) {
    if (!userId) return null;
    
    try {
      const { data, error } = await supabase
        .from('auth.users')
        .select('id, email, raw_user_meta_data')
        .eq('id', userId)
        .single();
        
      if (error) {
        console.error('Error fetching user details:', error);
        return null;
      }
      
      return {
        id: data.id,
        email: data.email,
        name: data.raw_user_meta_data?.name || data.email || 'Usuário',
        avatar: data.raw_user_meta_data?.avatar_url
      };
    } catch (err) {
      console.error('Error fetching user details:', err);
      return null;
    }
  },
  
  // Método para buscar múltiplos usuários de uma vez
  async getUsersDetails(userIds: string[]) {
    if (!userIds || userIds.length === 0) return [];
    
    try {
      console.log('Fetching details for users:', userIds);
      
      // Verificar se os IDs são válidos
      const validUserIds = userIds.filter(id => id && typeof id === 'string' && id.trim() !== '');
      
      if (validUserIds.length === 0) {
        console.warn('No valid user IDs to fetch');
        return [];
      }
      
      // Buscar usuários em lotes para evitar problemas com consultas muito grandes
      const batchSize = 10;
      let allUsers = [];
      
      for (let i = 0; i < validUserIds.length; i += batchSize) {
        const batch = validUserIds.slice(i, i + batchSize);
        console.log('Fetching batch:', batch);
        
        const { data, error } = await supabase
          .from('user_profile')
          .select('auth_id, email, name, avatar_url')
          .in('auth_id', batch);
          
        if (error) {
          console.error('Error fetching users batch:', error);
          continue;
        }
        
        if (data) {
          allUsers = [...allUsers, ...data];
        }
      }
      
      console.log('All users fetched:', allUsers);
      
      return allUsers.map(user => ({
        id: user.auth_id,
        email: user.email,
        name: user.name || user.email || 'Usuário',
        avatar: user.avatar_url
      }));
    } catch (err) {
      console.error('Exception when fetching users details:', err);
      return [];
    }
  }
}; 